import { CSSProperties, forwardRef, ReactNode, Ref, useEffect, useRef } from 'react';
import {
  Label,
  TextArea as AriaTextArea,
  TextAreaProps as AriaTextAreaProps,
  TextField,
} from 'react-aria-components';

import Message from '@/design_system/Message';
import { createBEMClasses } from '@/utils/classname';

import './TextArea.css';

const { block, element } = createBEMClasses('text-area');

export interface TextAreaProps extends AriaTextAreaProps {
  label?: ReactNode;
  isInvalid?: boolean;
  error?: string;
  ariaLabel?: string;
  onChange?: (value: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isReadOnly?: boolean;
  actions?: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  scrollToAndFocusOnRender?: boolean;
  size?: 'medium' | 'large';
  labelFontWeight?: 'medium' | 'regular';
  isNativeTextarea?: boolean;
  autoGrow?: boolean;
}

const TextArea = forwardRef(function TextArea(
  {
    isInvalid,
    error,
    value,
    onChange,
    label,
    ariaLabel,
    isReadOnly,
    actions,
    className,
    style,
    scrollToAndFocusOnRender,
    size = 'medium',
    labelFontWeight = 'medium',
    isNativeTextarea,
    autoGrow,
    ...props
  }: TextAreaProps,
  refProp: Ref<HTMLTextAreaElement>
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollToAndFocusOnRender) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' });

      if (refProp && 'current' in refProp) {
        refProp.current?.focus();
      } else {
        textAreaRef.current?.focus();
      }
    }
  }, [refProp, scrollToAndFocusOnRender]);

  const TextAreaElement = isNativeTextarea ? 'textarea' : AriaTextArea;

  return (
    <TextField
      className={element('wrapper', { size }, className)}
      isReadOnly={isReadOnly}
      aria-label={ariaLabel}
      style={style}
      ref={containerRef}
    >
      {label && (
        <Label
          className={
            size === 'large'
              ? labelFontWeight === 'medium'
                ? 'paragraph-50-medium'
                : 'paragraph-50-regular'
              : labelFontWeight === 'medium'
                ? 'label-100-medium'
                : 'label-100-regular'
          }
        >
          {label}
        </Label>
      )}
      <TextAreaElement
        className={block({ invalid: isInvalid || !!error, 'auto-grow': autoGrow })}
        value={value}
        onChange={(event) => onChange?.(event)}
        ref={refProp ?? textAreaRef}
        {...props}
      />
      {actions && <div className={element('actions')}>{actions}</div>}
      {!!error && <Message type="error">{error}</Message>}
    </TextField>
  );
});

export default TextArea;
